import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import { Grid } from '@material-ui/core';
import { useFormikContext, getIn } from 'formik';
import { lookupsJson } from '@ourbranch/lookups';
import Field from 'core/components/form/form.v2';
import { Label } from 'core/components/label';
import { LabelTooltip } from 'core/components/label-tooltip';
import { getConstructionTypeExemptPropertyTypes } from 'common/helpers/farm-helpers';

import useStyles from './mobile-home-form.styles';

// Form options constants
const electricalServiceOptions = lookupsJson.farm.electricalService;
const electricalTypesOptions = lookupsJson.farm.electricalType;
const fireProtectiveDevicesOptions = lookupsJson.farm.fireProtectiveDevice;
const heatingTypeOptions = lookupsJson.farm.heatingType;
const mobileHomeQualityGradeOptions = lookupsJson.farm.mobileHomeQualityGrade;
const heatingSourceOptions = lookupsJson.farm.primaryHeatingSourceOptions;
const tieDownTypeOptions = lookupsJson.farm.tieDownTypes;
const mobileHomeConfigOptions = lookupsJson.farm.mobileHomeConfig;

const constructionTypeOptions = lookupsJson.farm.dwellingConstructionType;
const roofShapeOptions = lookupsJson.roofShape;
const roofTypeOptions = lookupsJson.farm.mobileHomeRoofType;
const theftPreventionDevicesOptions = lookupsJson.farm.theftProtectiveDevice;

const MobileHomeForm = ({
  basePath,
  isSelectedHeatingSourceUnitCountBased,
  isSelectedSecondaryHeatingSourceUnitCountBased,
  disableRiskForm,
  initialBuilding,
  initialConnectedHome,
  toastRCEWarning,
  state,
  riskType,
  setFieldValue
}) => {
  const classes = useStyles();
  const formikContext = useFormikContext() || {};
  const { values = {} } = formikContext;
  const areasUnderConstruction = getIn(values, `${basePath}.building.areasUnderConstruction`);
  const showConstructionType = !getConstructionTypeExemptPropertyTypes(state, riskType).includes('MH');

  // Set default value for ownership for new risks only
  useEffect(() => {
    if (setFieldValue && basePath) {
      // Only set default if value doesn't already exist (new risk)
      if (!initialBuilding?.ownershipStatus) {
        setFieldValue(`${basePath}.building.ownershipStatus`, 'P');
      }
    }
  }, [basePath, setFieldValue, initialBuilding?.ownershipStatus]);

  return (
    <>
      <Grid item xs={12}>
        <Label type="infoTitleSmall">Property</Label>
      </Grid>
      <Field
        xs={showConstructionType ? 4 : 6}
        name={`${basePath}.building.yearBuilt`}
        id={`${basePath}.building.yearBuilt`}
        type="numeric"
        label="Year built in"
        mode="light"
        disabled={disableRiskForm}
        onChange={(val) => {
          if (val !== initialBuilding?.yearBuilt) {
            toastRCEWarning?.();
          }
        }}
      />
      <Field
        xs={showConstructionType ? 4 : 6}
        type="numeric"
        id={`${basePath}.building.sqFt`}
        name={`${basePath}.building.sqFt`}
        mode="light"
        label="Total finished sqft"
        disabled={disableRiskForm}
        onChange={(val) => {
          if (val !== initialBuilding?.sqFt) {
            toastRCEWarning?.();
          }
        }}
      />
      {showConstructionType && (
        <Field
          xs={4}
          type="select"
          options={constructionTypeOptions}
          id={`${basePath}.building.constructionType`}
          name={`${basePath}.building.constructionType`}
          mode="light"
          label="Construction type"
          disabled={disableRiskForm}
          onChange={(val) => {
            if (val !== initialBuilding?.constructionType) {
              toastRCEWarning?.();
            }
          }}
        />
      )}
      <Field
        xs={6}
        type="select"
        options={mobileHomeQualityGradeOptions}
        id={`${basePath}.building.mobileHomeQualityGrade`}
        name={`${basePath}.building.mobileHomeQualityGrade`}
        mode="light"
        label="Home quality"
        disabled={disableRiskForm}
        onChange={(val) => {
          if (val !== initialBuilding?.mobileHomeQualityGrade) {
            toastRCEWarning?.();
          }
        }}
      />
      <Field
        xs={6}
        type="select"
        options={mobileHomeConfigOptions}
        id={`${basePath}.building.mobileHomeConfig`}
        name={`${basePath}.building.mobileHomeConfig`}
        mode="light"
        label="Mobile home config"
        disabled={disableRiskForm}
        onChange={(val) => {
          if (val !== initialBuilding?.mobileHomeConfig) {
            toastRCEWarning?.();
          }
        }}
      />
      <Field
        xs={4}
        type="numeric"
        id={`${basePath}.building.roofYear`}
        name={`${basePath}.building.roofYear`}
        mode="light"
        label="Roof year"
        disabled={disableRiskForm}
        onChange={(val) => {
          if (val !== initialBuilding?.roofYear) {
            toastRCEWarning?.();
          }
        }}
      />
      <Field
        xs={4}
        id={`${basePath}.building.roofType`}
        name={`${basePath}.building.roofType`}
        type="select"
        options={roofTypeOptions}
        mode="light"
        label="Roof type"
        disabled={disableRiskForm}
        onChange={(val) => {
          if (val !== initialBuilding?.roofType) {
            toastRCEWarning?.();
          }
        }}
      />
      <Field
        xs={4}
        id={`${basePath}.building.roofShape`}
        name={`${basePath}.building.roofShape`}
        type="select"
        options={roofShapeOptions}
        mode="light"
        label="Roof shape"
        disabled={disableRiskForm}
        onChange={(val) => {
          if (val !== initialBuilding?.roofShape) {
            toastRCEWarning?.();
          }
        }}
      />
      <Field
        xs={6}
        type="checkbox"
        id={`${basePath}.building.areasUnderConstruction`}
        name={`${basePath}.building.areasUnderConstruction`}
        mode="light"
        label="Renovations"
        disabled={disableRiskForm}
        onChange={(val) => {
          if (val !== initialBuilding?.areasUnderConstruction) {
            toastRCEWarning?.();
          }
        }}
      />
      <Field
        xs={6}
        type="select"
        id={`${basePath}.building.tieDownType`}
        name={`${basePath}.building.tieDownType`}
        mode="light"
        label="Tie down type"
        options={tieDownTypeOptions}
        disabled={disableRiskForm}
        onChange={(val) => {
          if (val !== initialBuilding?.tieDownType) {
            toastRCEWarning?.();
          }
        }}
      />
      <Grid item xs={12}>
        <LabelTooltip
          label="Solid Fuel-Burning Appliances"
          tooltip={{
            label: 'More Info',
            onHoverText:
              'Solid Fuel or wood heat examples include standard fireplace, fireplace with insert, indoor solid fuel or wood heat (including pellet stoves), outside solid fuel or wood heat. Barrel or Homemade stoves are Not Acceptable.'
          }}
          mode="light"
        >
          <Field
            type="numeric"
            id={`${basePath}.building.numSolidFuelBurningAppliances`}
            name={`${basePath}.building.numSolidFuelBurningAppliances`}
            mode="light"
            format={{
              allowNegative: false
            }}
            disabled={disableRiskForm}
            onChange={(val) => {
              if (val !== initialBuilding?.numSolidFuelBurningAppliances) {
                toastRCEWarning?.();
              }
            }}
          />
        </LabelTooltip>
      </Grid>
      <Field
        xs={3}
        id={`${basePath}.building.primaryHeatingSource`}
        name={`${basePath}.building.primaryHeatingSource`}
        type="select"
        options={heatingSourceOptions}
        mode="light"
        label="Primary heating"
        disabled={disableRiskForm}
        onChange={(val) => {
          if (val !== initialBuilding?.primaryHeatingSource) {
            toastRCEWarning?.();
          }
        }}
      />
      <Field
        xs={3}
        type="numeric"
        id={`${basePath}.building.numHeatingSources`}
        name={`${basePath}.building.numHeatingSources`}
        mode="light"
        label="Heating units"
        disabled={disableRiskForm || !isSelectedHeatingSourceUnitCountBased}
        onChange={(val) => {
          if (val !== initialBuilding.numHeatingSources) {
            toastRCEWarning?.();
          }
        }}
      />
      <Field
        xs={3}
        id={`${basePath}.building.secondaryHeatingSource`}
        name={`${basePath}.building.secondaryHeatingSource`}
        type="select"
        options={heatingSourceOptions}
        mode="light"
        label="Secondary heating"
        disabled={disableRiskForm}
        onChange={(val) => {
          if (val !== initialBuilding?.secondaryHeatingSource) {
            toastRCEWarning?.();
          }
        }}
      />
      <Field
        xs={3}
        type="numeric"
        id={`${basePath}.building.numSecondaryHeatingSources`}
        name={`${basePath}.building.numSecondaryHeatingSources`}
        mode="light"
        label="Heating units"
        disabled={disableRiskForm || !isSelectedSecondaryHeatingSourceUnitCountBased}
        onChange={(val) => {
          if (val !== initialBuilding.numSecondaryHeatingSources) {
            toastRCEWarning?.();
          }
        }}
      />
      {areasUnderConstruction && (
        <>
          <Field
            xs={4}
            id={`${basePath}.building.renovation.electricalType`}
            name={`${basePath}.building.renovation.electricalType`}
            type="select"
            options={electricalTypesOptions}
            mode="light"
            label="Electric type"
            disabled={disableRiskForm}
            onChange={(val) => {
              if (val !== initialBuilding?.renovation?.electricalType) {
                toastRCEWarning?.();
              }
            }}
          />
          <Field
            xs={4}
            id={`${basePath}.building.renovation.electricalService`}
            name={`${basePath}.building.renovation.electricalService`}
            type="select"
            options={electricalServiceOptions}
            mode="light"
            label="Electrical service update"
            disabled={disableRiskForm}
            onChange={(val) => {
              if (val !== initialBuilding?.renovation?.electricalService) {
                toastRCEWarning?.();
              }
            }}
          />
          <Field
            xs={4}
            id={`${basePath}.building.renovation.heatingType`}
            name={`${basePath}.building.renovation.heatingType`}
            type="select"
            options={heatingTypeOptions}
            mode="light"
            label="Heating Type"
            disabled={disableRiskForm}
            onChange={(val) => {
              if (val !== initialBuilding?.renovation?.heatingType) {
                toastRCEWarning?.();
              }
            }}
          />
          <Field
            xs={4}
            type="numeric"
            id={`${basePath}.building.renovation.heatingYear`}
            name={`${basePath}.building.renovation.heatingYear`}
            mode="light"
            label="Heating year"
            disabled={disableRiskForm}
            onChange={(val) => {
              if (val !== initialBuilding?.renovation?.heatingYear) {
                toastRCEWarning?.();
              }
            }}
          />
          <Field
            xs={4}
            type="numeric"
            id={`${basePath}.building.renovation.coolingYear`}
            name={`${basePath}.building.renovation.coolingYear`}
            mode="light"
            label="AC year"
            disabled={disableRiskForm}
            onChange={(val) => {
              if (val !== initialBuilding?.renovation?.coolingYear) {
                toastRCEWarning?.();
              }
            }}
          />
          <Field
            xs={4}
            type="numeric"
            id={`${basePath}.building.renovation.plumbingYear`}
            name={`${basePath}.building.renovation.plumbingYear`}
            mode="light"
            label="Plumbing year"
            disabled={disableRiskForm}
            onChange={(val) => {
              if (val !== initialBuilding?.renovation?.plumbingYear) {
                toastRCEWarning?.();
              }
            }}
          />
        </>
      )}
      <Field
        xs={6}
        id={`${basePath}.connectedHome.theftPreventionDeviceType`}
        name={`${basePath}.connectedHome.theftPreventionDeviceType`}
        type="select"
        options={theftPreventionDevicesOptions}
        mode="light"
        label="Theft protective device"
        disabled={disableRiskForm}
        onChange={(val) => {
          if (val !== initialConnectedHome?.theftPreventionDeviceType) {
            toastRCEWarning?.();
          }
        }}
      />
      <Field
        xs={6}
        id={`${basePath}.connectedHome.fireProtectiveDeviceType`}
        name={`${basePath}.connectedHome.fireProtectiveDeviceType`}
        options={fireProtectiveDevicesOptions}
        type="select"
        mode="light"
        label="Fire protective device"
        disabled={disableRiskForm}
        onChange={(val) => {
          if (val !== initialConnectedHome?.fireProtectiveDeviceType) {
            toastRCEWarning?.();
          }
        }}
      />
      <Grid item xs={6} className={classes.checkboxGridItem}>
        <Field
          name={`${basePath}.building.enclosedMasonryFoundation`}
          id={`${basePath}.building.enclosedMasonryFoundation`}
          type="checkbox"
          label="Enclosed masonry foundation?"
          mode="light"
          disabled={disableRiskForm}
          className={classes.checkboxContainer}
          onChange={(val) => {
            if (val !== initialBuilding?.enclosedMasonryFoundation) {
              toastRCEWarning?.();
            }
          }}
        />
      </Grid>
      <Grid item xs={6} className={classes.checkboxGridItem}>
        <Field
          name={`${basePath}.building.fullSkirtingAroundBase`}
          id={`${basePath}.building.fullSkirtingAroundBase`}
          type="checkbox"
          label="Is there a full skirting around the base?"
          mode="light"
          disabled={disableRiskForm}
          className={classes.checkboxContainer}
          onChange={(val) => {
            if (val !== initialBuilding?.fullSkirtingAroundBase) {
              toastRCEWarning?.();
            }
          }}
        />
      </Grid>
      <Grid item xs={6} className={classes.checkboxGridItem}>
        <Field
          name={`${basePath}.connectedHome.internetMonitoring`}
          id={`${basePath}.connectedHome.internetMonitoring`}
          type="checkbox"
          label="Internet connected monitoring device(s)?"
          mode="light"
          disabled={disableRiskForm}
          className={classes.checkboxContainer}
          onChange={(val) => {
            if (val !== initialConnectedHome?.internetMonitoring) {
              toastRCEWarning?.();
            }
          }}
        />
      </Grid>
    </>
  );
};

MobileHomeForm.propTypes = {
  basePath: PropTypes.string.isRequired,
  isSelectedHeatingSourceUnitCountBased: PropTypes.bool,
  isSelectedSecondaryHeatingSourceUnitCountBased: PropTypes.bool,
  disableRiskForm: PropTypes.bool,
  initialBuilding: PropTypes.object,
  initialConnectedHome: PropTypes.object,
  toastRCEWarning: PropTypes.func,
  state: PropTypes.string,
  riskType: PropTypes.string,
  setFieldValue: PropTypes.func
};

export default MobileHomeForm;
